import React, { createContext, useState, useContext, useEffect } from 'react';
import { authAPI } from '../api/client';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentCompanyId, setCurrentCompanyId] = useState(null);

  // Initialize from localStorage
  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    const storedCompanyId = localStorage.getItem('currentCompanyId');

    if (storedToken && storedUser && storedUser !== 'undefined' && storedUser !== 'null') {
      try {
        setToken(storedToken);
        const parsedUser = JSON.parse(storedUser);
        setUser(parsedUser);
        
        // Ensure we have a valid company ID
        if (storedCompanyId && storedCompanyId !== 'null' && storedCompanyId !== 'undefined') {
          setCurrentCompanyId(storedCompanyId);
        } else if (parsedUser.defaultCompany?.companyId) {
          // Fallback to user's default company
          setCurrentCompanyId(parsedUser.defaultCompany.companyId);
          localStorage.setItem('currentCompanyId', parsedUser.defaultCompany.companyId);
        }

        // Silently refresh user profile from server to keep data fresh
        authAPI.me().then(res => {
          if (res?.data?.user) {
            const fresh = res.data.user;
            setUser(fresh);
            localStorage.setItem('user', JSON.stringify(fresh));
            if (res.data.currentCompanyId) {
              setCurrentCompanyId(res.data.currentCompanyId);
              localStorage.setItem('currentCompanyId', res.data.currentCompanyId);
            }
          }
        }).catch(() => { /* token expired or network error — ignore, stale data still works */ });
      } catch (err) {
        console.error('Error parsing stored user data:', err);
        // Clear invalid data
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('currentCompanyId');
      }
    }
    setLoading(false);
  }, []);

  const login = async (credentials) => {
    try {
      const response = await authAPI.login(credentials);
      const { token, user: userData } = response.data;

      setToken(token);
      setUser(userData);
      
      // Set default company
      const defaultCompanyId = userData.defaultCompany?.companyId;
      setCurrentCompanyId(defaultCompanyId);

      // Persist to localStorage
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(userData));
      localStorage.setItem('currentCompanyId', defaultCompanyId);

      return { success: true };
    } catch (error) {
      console.error('Login error:', error);
      return { 
        success: false, 
        error: error.response?.data?.message || 'Login failed' 
      };
    }
  };

  const logout = async () => {
    try {
      await authAPI.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setToken(null);
      setUser(null);
      setCurrentCompanyId(null);
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('currentCompanyId');
    }
  };

  const switchCompany = async (companyId) => {
    try {
      await authAPI.switchCompany(companyId);
      setCurrentCompanyId(companyId);
      localStorage.setItem('currentCompanyId', companyId);
      return { success: true };
    } catch (error) {
      console.error('Switch company error:', error);
      return { 
        success: false, 
        error: error.response?.data?.message || 'Failed to switch company' 
      };
    }
  };

  const value = {
    user,
    token,
    loading,
    currentCompanyId,
    isAuthenticated: !!token && !!user,
    isSuperAdmin: user?.isSuperAdmin || user?.username === 'superadmin' || false,
    login,
    logout,
    switchCompany,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
